import styled from 'styled-components';
import { useParams, Link, Navigate } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiApple, mdiWeb, mdiArrowLeft, mdiCheck } from '@mdi/js';
import { getAppById, apps } from '../../data/apps';
import { Section, SectionHeader } from '../ui/Section';
import { GlassCard } from '../ui/GlassCard';
import { TechStackBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Container } from '../ui/Container';
import { CTASection } from '../sections/CTASection';
import { PageHead } from '../seo';

const PageWrapper = styled.div`
  padding-top: 73px;
`;

const HeroSection = styled.section<{ $color: string }>`
  padding: var(--space-16) 0;
  background: linear-gradient(
    135deg,
    ${props => props.$color}15 0%,
    transparent 50%
  );
  position: relative;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
  margin-bottom: var(--space-6);
  text-decoration: none;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--color-text-primary);
  }
`;

const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-12);
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const HeroText = styled.div``;

const AppIconLarge = styled.div<{ $color: string }>`
  width: 80px;
  height: 80px;
  border-radius: var(--radius-xl);
  background: ${props => props.$color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-6);
  overflow: hidden;

  @media (max-width: 968px) {
    margin: 0 auto var(--space-6);
  }
`;

const AppLogoImg = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;
`;

const AppTitle = styled.h1`
  font-size: var(--text-5xl);
  margin-bottom: var(--space-2);
`;

const AppTagline = styled.p`
  font-size: var(--text-xl);
  color: var(--color-text-secondary);
  margin-bottom: var(--space-6);
`;

const PlatformBadges = styled.div`
  display: flex;
  gap: var(--space-2);
  margin-bottom: var(--space-6);

  @media (max-width: 968px) {
    justify-content: center;
  }
`;

const PlatformBadge = styled.span<{ $color: string }>`
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-full);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  background: ${props => props.$color}15;
  color: ${props => props.$color};
`;

const AppDescription = styled.p`
  font-size: var(--text-lg);
  line-height: var(--leading-relaxed);
  color: var(--color-text-secondary);
  margin-bottom: var(--space-8);
`;

const CTAButtons = styled.div`
  display: flex;
  gap: var(--space-4);
  flex-wrap: wrap;

  @media (max-width: 968px) {
    justify-content: center;
  }
`;

const HeroVisual = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 968px) {
    display: none;
  }
`;

const MockupPlaceholder = styled.div<{ $color: string }>`
  width: 300px;
  height: 400px;
  border-radius: var(--radius-2xl);
  background: linear-gradient(
    145deg,
    ${props => props.$color}30,
    ${props => props.$color}10
  );
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 80px;
  box-shadow: var(--shadow-xl);
`;

const MockupWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
`;

const LaptopImage = styled.img`
  width: 100%;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.2));
`;

const PhoneImage = styled.img`
  position: absolute;
  right: -10%;
  bottom: -10%;
  width: 35%;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.25));
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-6);

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(GlassCard)``;

const FeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  background: var(--color-success);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-4);
`;

const FeatureTitle = styled.h3`
  font-size: var(--text-lg);
  margin-bottom: var(--space-2);
`;

const TechSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  justify-content: center;
`;

const OtherApps = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-4);

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const OtherAppCard = styled(Link)<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4);
  border-radius: var(--radius-lg);
  background: var(--glass-background);
  border: 1px solid var(--glass-border);
  text-decoration: none;
  transition: all var(--transition-base);

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
    border-color: ${props => props.$color};
  }
`;

const OtherAppIcon = styled.div<{ $color: string }>`
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  background: ${props => props.$color}15;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const OtherAppLogo = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;
`;

const OtherAppInfo = styled.div``;

const OtherAppName = styled.div`
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
`;

const OtherAppTagline = styled.div`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
`;

const platformLabels: Record<string, string> = {
  web: 'Web App',
  ios: 'iOS App',
  both: 'Web & iOS',
};

export function AppDetail() {
  const { appId } = useParams<{ appId: string }>();
  const app = appId ? getAppById(appId) : undefined;

  if (!app) {
    return <Navigate to="/products" replace />;
  }

  // Substrate is presented standalone (its own page) and must not appear as a
  // sibling on other apps' detail pages, and we don't cross-reference apps from
  // its page. Filter substrate out of every AppDetail "Explore More Apps" rail.
  const otherApps = apps.filter(a => a.id !== app.id && a.id !== 'substrate').slice(0, 4);

  const ogImage = app.imageLaptop
    ? `https://portfolio.data26.solutions${app.imageLaptop}`
    : `https://portfolio.data26.solutions${app.logo}`;

  return (
    <PageWrapper>
      <PageHead
        title={`${app.name} — ${app.tagline}`}
        description={app.description}
        ogImage={ogImage}
      />
      <HeroSection $color={app.accentColor}>
        <Container>
          <BackLink to="/products">
            <Icon path={mdiArrowLeft} size={0.8} />
            Back to Products
          </BackLink>

          <HeroContent>
            <HeroText>
              <AppIconLarge $color={app.accentColor}>
                <AppLogoImg src={app.logo} alt={`${app.name} logo`} />
              </AppIconLarge>
              <AppTitle>{app.name}</AppTitle>
              <AppTagline>{app.tagline}</AppTagline>

              <PlatformBadges>
                <PlatformBadge $color={app.accentColor}>
                  {platformLabels[app.platform]}
                </PlatformBadge>
                <PlatformBadge $color="var(--color-success)">
                  {app.status === 'live' ? 'Available Now' : app.status}
                </PlatformBadge>
              </PlatformBadges>

              <AppDescription>{app.description}</AppDescription>

              <CTAButtons>
                {app.links.web && (
                  <Button as="a" href={app.links.web} target="_blank" rel="noopener noreferrer">
                    <Icon path={mdiWeb} size={0.9} />
                    Open Web App
                  </Button>
                )}
                {app.links.appStore && app.links.appStore !== '#' && (
                  <Button as="a" href={app.links.appStore} target="_blank" rel="noopener noreferrer" variant="secondary">
                    <Icon path={mdiApple} size={0.9} />
                    App Store
                  </Button>
                )}
              </CTAButtons>
            </HeroText>

            <HeroVisual>
              {app.imageLaptop ? (
                <MockupWrapper>
                  <LaptopImage src={app.imageLaptop} alt={`${app.name} on laptop`} />
                  {app.imagePhone && (
                    <PhoneImage src={app.imagePhone} alt={`${app.name} on mobile`} />
                  )}
                </MockupWrapper>
              ) : (
                <MockupPlaceholder $color={app.accentColor}>
                  <AppLogoImg src={app.logo} alt={`${app.name} logo`} style={{ width: '60%', height: '60%', objectFit: 'contain' }} />
                </MockupPlaceholder>
              )}
            </HeroVisual>
          </HeroContent>
        </Container>
      </HeroSection>

      <Section>
        <SectionHeader
          title="Features"
          subtitle={`Everything ${app.name} has to offer.`}
          centered
        />
        <FeaturesGrid>
          {app.features.map((feature, index) => (
            <FeatureCard key={index}>
              <FeatureIcon>
                <Icon path={mdiCheck} size={1} />
              </FeatureIcon>
              <FeatureTitle>{feature}</FeatureTitle>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </Section>

      <Section background="secondary">
        <SectionHeader
          title="Built With"
          subtitle="Modern technologies for a seamless experience."
          centered
        />
        <TechSection>
          {app.techStack.map(tech => (
            <TechStackBadge key={tech} tech={tech} size="md" />
          ))}
        </TechSection>
      </Section>

      <Section>
        <SectionHeader
          title="Explore More Apps"
          subtitle="Check out other apps in our ecosystem."
          centered
        />
        <OtherApps>
          {otherApps.map(otherApp => (
            <OtherAppCard key={otherApp.id} to={`/products/${otherApp.id}`} $color={otherApp.accentColor}>
              <OtherAppIcon $color={otherApp.accentColor}>
                <OtherAppLogo src={otherApp.logo} alt={`${otherApp.name} logo`} />
              </OtherAppIcon>
              <OtherAppInfo>
                <OtherAppName>{otherApp.name}</OtherAppName>
                <OtherAppTagline>{otherApp.tagline}</OtherAppTagline>
              </OtherAppInfo>
            </OtherAppCard>
          ))}
        </OtherApps>
      </Section>

      <CTASection
        title="Questions or Feedback?"
        subtitle={`We'd love to hear what you think about ${app.name}.`}
        primaryCTA={{ label: 'Contact Us', to: '/contact' }}
        secondaryCTA={{ label: 'View All Apps', to: '/products' }}
      />
    </PageWrapper>
  );
}
